/**
 * useSavedPrompts — the viewer's prompt library, live and optimistic
 * (2026-09-17). The `useThreadCategories` contract: the local list changes at
 * once, the write settles behind it, and a failed write is undone by the next
 * snapshot rather than by bookkeeping nobody can see.
 *
 * This hook is where `firebase/auth` enters the graph (through the repository's
 * write guard), which is why the picker takes the API it returns as a PROP
 * instead of importing it: a component the chat-isolation suites render must
 * not reach `firebase/auth`, whose import touches global `fetch` and takes the
 * whole suite down before a single test runs (2026-09-11). The
 * `RoutineManagerPanel` / `AccountRoutinesApi` arrangement, for the same reason.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  createSavedPrompt,
  deleteSavedPrompt,
  markSavedPromptUsed,
  renamePromptCategory,
  subscribeSavedPrompts,
  subscribeSharedPrompts,
  updateSavedPrompt,
} from '../repositories/savedPromptsRepository';
import {
  deleteSharedPrompt,
  republishPrompt,
  sharePrompt,
  unsharePrompt,
  updateSharedPrompt,
} from '../services/promptLibrary/client';
import { useRbac } from './useRbac';
import {
  MAX_PROMPTS_PER_PERSON,
  orderPrompts,
  promptCategories,
  type PromptProblem,
  type SavedPrompt,
  type SavedPromptDraft,
  type SharedPrompt,
} from '../types/savedPrompts';

/** What the picker and the editor are handed. Everything they can do is here. */
export interface SavedPromptsApi {
  /** Ordered as the picker shows them: recently used, then recently changed. */
  prompts: SavedPrompt[];
  /** Distinct category names over those prompts, folded and alphabetical. */
  categories: string[];
  /** True until the first snapshot lands. Absence proves nothing before then. */
  loading: boolean;
  /** The last read failed; the list below is the last one we knew. */
  degraded: boolean;
  /** The library can take another prompt. */
  hasRoom: boolean;
  max: number;
  create: (draft: SavedPromptDraft) => Promise<PromptProblem | null>;
  update: (id: string, draft: SavedPromptDraft) => Promise<PromptProblem | null>;
  remove: (id: string) => Promise<boolean>;
  renameCategory: (from: string, to: string) => Promise<boolean>;
  /** Record that a prompt was just put into a draft. Never awaited by a caller. */
  markUsed: (id: string) => void;

  // ── The workspace's shelf ────────────────────────────────────────────────
  /** What the workspace has published. Empty when the viewer cannot see it. */
  team: SharedPrompt[];
  /** True until the team shelf has an answer — including "you cannot see it". */
  teamLoading: boolean;
  /** The viewer, so a row can tell whose prompt it is (canEditSharedPrompt). */
  viewerUid: string;
  /** The viewer may publish their own prompts here. */
  canPublish: boolean;
  /** The viewer may change or remove ANYONE's shared prompt. */
  canCurate: boolean;
  /**
   * Every workspace action answers the same way: `null` when it landed, or the
   * SERVER's own second-person refusal, verbatim. No client-side sentence is
   * invented for a verdict the server already worded — two sets of words drift,
   * and the person then reads whichever one is stale.
   */
  share: (promptId: string) => Promise<string | null>;
  republish: (promptId: string) => Promise<string | null>;
  unshare: (promptId: string) => Promise<string | null>;
  updateShared: (sharedPromptId: string, draft: SavedPromptDraft) => Promise<string | null>;
  deleteShared: (sharedPromptId: string) => Promise<string | null>;
  /** Keep a colleague's shared prompt as one of your own. */
  saveCopy: (shared: SharedPrompt) => Promise<PromptProblem | null>;
}

const EMPTY: SavedPrompt[] = [];
const EMPTY_TEAM: SharedPrompt[] = [];

/** No workspace, or nothing configured — every action says so the same way. */
const NO_WORKSPACE = 'There is no workspace to publish this to.';

export function useSavedPrompts(
  uid: string | null | undefined,
  accountId?: string | null,
): SavedPromptsApi {
  const [prompts, setPrompts] = useState<SavedPrompt[]>(EMPTY);
  const [team, setTeam] = useState<SharedPrompt[]>(EMPTY_TEAM);
  const [teamLoading, setTeamLoading] = useState(false);
  const [loading, setLoading] = useState<boolean>(Boolean(uid));
  const [degraded, setDegraded] = useState(false);
  const promptsRef = useRef<SavedPrompt[]>(EMPTY);
  promptsRef.current = prompts;

  useEffect(() => {
    if (!uid) {
      setPrompts(EMPTY);
      setLoading(false);
      setDegraded(false);
      return undefined;
    }
    setLoading(true);
    let live = true;
    let stop: (() => void) | undefined;
    try {
      stop = subscribeSavedPrompts(
        uid,
        (rows) => {
          if (!live) return;
          setPrompts(rows);
          setLoading(false);
          setDegraded(false);
        },
        () => {
          if (!live) return;
          // Keep whatever we already showed — a picker that blanks mid-compose
          // is worse than one that is briefly stale.
          setLoading(false);
          setDegraded(true);
        },
      );
    } catch {
      setLoading(false);
      setDegraded(true);
    }
    return () => { live = false; stop?.(); };
  }, [uid]);

  // ── Who the viewer is, here ───────────────────────────────────────────────
  // The client's view only: every core re-checks on the server, so an owner
  // whose catalog-v14 migration has not fired yet sees the button and the core
  // decides. The reverse — hiding a control from somebody who may use it —
  // is the failure that sends people to a settings page for no reason.
  const rbac = useRbac();
  const canView = rbac.isOwner || rbac.permissions.has('prompts.library.view');
  const canPublish = rbac.isOwner || rbac.permissions.has('prompts.library.publish');
  const canCurate = rbac.isOwner || rbac.permissions.has('prompts.library.manage');

  useEffect(() => {
    // Nothing to subscribe to, or a role that the rule will refuse anyway:
    // answer "no team shelf" without opening a listener that is certain to be
    // denied. While RBAC is still resolving we simply wait — the person's own
    // library is already on screen, which is what the picker opens for.
    if (!accountId || !uid || rbac.loading || !canView) {
      setTeam(EMPTY_TEAM);
      setTeamLoading(Boolean(accountId) && Boolean(uid) && rbac.loading);
      return undefined;
    }
    setTeamLoading(true);
    let live = true;
    let stop: (() => void) | undefined;
    try {
      stop = subscribeSharedPrompts(
        accountId,
        (rows) => {
          if (!live) return;
          setTeam(rows);
          setTeamLoading(false);
        },
        () => {
          // A refusal here is ordinary — a role without the key, a workspace
          // that has published nothing. Either way the honest answer is an
          // empty shelf, never an error in front of somebody composing.
          if (!live) return;
          setTeam(EMPTY_TEAM);
          setTeamLoading(false);
        },
      );
    } catch {
      setTeam(EMPTY_TEAM);
      setTeamLoading(false);
    }
    return () => { live = false; stop?.(); };
  }, [accountId, uid, rbac.loading, canView]);

  /** Apply now, let the snapshot settle it, and let a failure undo it. */
  const optimistic = useCallback((next: SavedPrompt[]) => {
    setPrompts(orderPrompts(next));
  }, []);

  const create = useCallback(async (draft: SavedPromptDraft): Promise<PromptProblem | null> => {
    if (!uid) return 'empty-body';
    const verdict = await createSavedPrompt(uid, draft, promptsRef.current.length).catch(() => null);
    if (!verdict) return 'library-full';
    if (!verdict.ok) return verdict.problem;
    optimistic([...promptsRef.current, verdict.value]);
    return null;
  }, [uid, optimistic]);

  const update = useCallback(async (id: string, draft: SavedPromptDraft): Promise<PromptProblem | null> => {
    if (!uid) return 'empty-body';
    const verdict = await updateSavedPrompt(uid, id, draft).catch(() => null);
    if (!verdict) return 'empty-body';
    if (!verdict.ok) return verdict.problem;
    optimistic(promptsRef.current.map((p) => (p.id === id ? { ...p, ...verdict.value } : p)));
    return null;
  }, [uid, optimistic]);

  const remove = useCallback(async (id: string): Promise<boolean> => {
    if (!uid) return false;
    const before = promptsRef.current;
    optimistic(before.filter((p) => p.id !== id));
    try {
      await deleteSavedPrompt(uid, id);
      return true;
    } catch {
      optimistic(before);
      return false;
    }
  }, [uid, optimistic]);

  const renameCategory = useCallback(async (from: string, to: string): Promise<boolean> => {
    if (!uid) return false;
    try {
      await renamePromptCategory(uid, from, to);
      return true;
    } catch {
      return false;
    }
  }, [uid]);

  const markUsed = useCallback((id: string) => {
    if (!uid) return;
    const row = promptsRef.current.find((p) => p.id === id);
    markSavedPromptUsed(uid, id, row?.useCount ?? 0);
    // Reflected locally too, so the order the person sees matches what they just
    // did without waiting on a round trip.
    optimistic(promptsRef.current.map((p) => (
      p.id === id ? { ...p, lastUsedAt: Date.now(), useCount: (p.useCount ?? 0) + 1 } : p
    )));
  }, [uid, optimistic]);

  /**
   * Every workspace action, one shape. The server's verdict is passed through
   * untouched; the live snapshots settle the lists, so nothing here guesses at
   * what the write did.
   */
  const runWorkspace = useCallback(async (
    run: (account: string) => Promise<{ ok: boolean; message: string }>,
  ): Promise<string | null> => {
    if (!accountId) return NO_WORKSPACE;
    const result = await run(accountId);
    return result.ok ? null : (result.message || NO_WORKSPACE);
  }, [accountId]);

  const share = useCallback((promptId: string) => (
    runWorkspace((account) => sharePrompt(account, promptId))
  ), [runWorkspace]);

  const republish = useCallback((promptId: string) => (
    runWorkspace((account) => republishPrompt(account, promptId))
  ), [runWorkspace]);

  const unshare = useCallback((promptId: string) => (
    runWorkspace((account) => unsharePrompt(account, promptId))
  ), [runWorkspace]);

  const updateShared = useCallback((sharedPromptId: string, draft: SavedPromptDraft) => (
    runWorkspace((account) => updateSharedPrompt(account, sharedPromptId, {
      title: String(draft.title ?? ''),
      body: String(draft.body ?? ''),
      category: draft.category == null ? undefined : String(draft.category),
    }))
  ), [runWorkspace]);

  const deleteShared = useCallback((sharedPromptId: string) => (
    runWorkspace((account) => deleteSharedPrompt(account, sharedPromptId))
  ), [runWorkspace]);

  /**
   * Keep a colleague's shared prompt as one of your own.
   *
   * A plain `create` — client-direct, optimistic, no round trip — because a
   * copy IS one of your own prompts: it starts at rev 1, carries no back-pointer
   * to the workspace, and changing it later changes nothing anybody else reads.
   * That independence is the whole point of copying rather than referencing.
   */
  const saveCopy = useCallback((shared: SharedPrompt) => (
    create({ title: shared.title, body: shared.body, category: shared.category })
  ), [create]);

  const categories = useMemo(() => promptCategories(prompts), [prompts]);

  return useMemo<SavedPromptsApi>(() => ({
    prompts,
    categories,
    loading,
    degraded,
    hasRoom: prompts.length < MAX_PROMPTS_PER_PERSON,
    max: MAX_PROMPTS_PER_PERSON,
    create,
    update,
    remove,
    renameCategory,
    markUsed,
    team,
    teamLoading,
    viewerUid: uid || '',
    canPublish,
    canCurate,
    share,
    republish,
    unshare,
    updateShared,
    deleteShared,
    saveCopy,
  }), [
    prompts, categories, loading, degraded, create, update, remove, renameCategory, markUsed,
    team, teamLoading, uid, canPublish, canCurate,
    share, republish, unshare, updateShared, deleteShared, saveCopy,
  ]);
}
